import React from "react";
import { Menu, X, User, Leaf } from "lucide-react";
import { useT } from "@/lib/LanguageContext";
import { SOCIAL_ICONS } from "@/components/site/socialIcons";
import { LangSwitch } from "../shared/TemplateShell";
import { NavMore, useInlineNavSplit } from "../shared/InlineNav";
import { useSiteContent } from "../shared/useSiteData";

/**
 * Warm / Organic — the theme's CHROME (header + footer), in one place.
 *
 * Extracted from OrganicTemplate so the homepage and the interior pages
 * (templates/ThemedPageLayout.jsx) render the SAME two components (BUG-006 / BUG-032).
 *
 * Idiom: no bar at all — the header floats over the page, brand set in the display serif
 * behind a leaf, everything soft-radius; the mobile panel is one of the theme's bento cards
 * (big radius, one corner cut) in the warm secondary ground.
 */

export const ORGANIC_MAIN_CLASS = "min-h-screen overflow-hidden";

/** The handwritten accent this theme uses for numbers and signatures. */
const SCRIPT = { fontFamily: "'Caveat', cursive" };

/**
 * The FR/EN/ES switch: the selected language is a FILLED chip, never a colour change alone.
 *
 * It used to be `activeClass="text-white"` — white type on this theme's near-white ground
 * (`--tpl-bg` #FDFBF7) and on the warm secondary the mobile panel sits on, so the language the
 * visitor was actually reading in was the one they could not see. The filled chip is the same
 * vocabulary as the theme's Book button (solid `--tpl-primary`, white type), and it reads on
 * both grounds because it brings its own.
 */
const LANG_ACTIVE = "bg-[var(--tpl-primary)] text-white";
const LANG_IDLE = "opacity-50 hover:opacity-100";

/**
 * How many of the ten nav links this theme can draw inline (BUG-045 — see
 * shared/InlineNav.jsx for why a fixed count and not a breakpoint).
 *
 * The budget: 14px semibold labels with `gap-6` (24px) cost ~99px each here, and the row
 * also carries the serif wordmark, the language chips, the round account button and the
 * pill CTA. Measured with all ten links, in French, on a 24-character practice name: the
 * bar overflowed by 574px at 1024px and by 303px at 1536px. Removing k links frees ~99k
 * and the "More" pill costs ~74, so 1024px needs k>=7 and 1536px needs k>=4 — hence 3 and
 * 6, each with a link's worth of margin.
 */
const INLINE_CAPACITY = { lg: 3, xl: 6 };

/**
 * The wordmark, SIZED TO ITS OWN LENGTH — the second half of this theme's BUG-045 fix.
 *
 * Capping the link row was not enough here. This theme sets its brand in the display serif at
 * `text-2xl` with no length tier and `shrink-0`, so it never gives: measured at 336px for
 * "Émilie Houde Naturopathe", the single widest item in the bar, and with the row's own
 * `max-w-7xl` the excess was a CONSTANT 28px at every width from 1366px up — i.e. no
 * breakpoint and no link count could have fixed it. Same treatment Luxe and Bold already
 * carry: tier down through the theme's own sizes as the name gets longer, with `truncate` as
 * the floor so a pathological name ellipsizes on one line instead of growing the header. A
 * short name (most practices) keeps `text-2xl` exactly as before.
 */
function brandClass(brand) {
  const n = (brand || "").length;
  if (n <= 14) return "text-2xl";
  if (n <= 22) return "text-xl";
  if (n <= 34) return "text-lg";
  return "text-base";
}

export const OrganicHeader = ({ toggles = {} }) => {
  const c = useSiteContent(toggles);
  const tt = useT("templates");
  const nav = c.navModel;
  const links = [...nav.sections, ...nav.pages];
  const { inline, overflow } = useInlineNavSplit(nav, INLINE_CAPACITY);
  const [open, setOpen] = React.useState(false);

  return (
    <header className="absolute top-0 inset-x-0 z-50" data-testid="site-navbar">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-6 flex items-center justify-between gap-6">
        <a
          href={nav.home}
          className={`tpl-head font-semibold flex items-center gap-2 min-w-0 ${brandClass(nav.brand)}`}
          data-testid="logo-home"
          title={nav.brand}
        >
          <Leaf size={20} className="shrink-0" style={{ color: "var(--tpl-accent)" }} />
          <span className="truncate">{nav.brand}</span>
        </a>
        <nav className="hidden lg:flex items-center gap-6 flex-1 justify-center">
          {inline.map((l) => (
            <a key={l.key} href={l.href} data-testid={`nav-link-${l.key}`} className="text-sm font-semibold opacity-70 hover:opacity-100 transition-opacity whitespace-nowrap">{l.label}</a>
          ))}
          <NavMore
            items={overflow}
            className="text-sm font-semibold opacity-70 hover:opacity-100 transition-opacity"
            menuClassName="rounded-[1.5rem] rounded-tr-lg shadow-[0_25px_60px_-35px_rgba(62,42,33,0.6)]"
            itemClassName="text-sm font-semibold opacity-80 hover:opacity-100"
            menuStyle={{ background: "var(--tpl-secondary)" }}
          />
        </nav>
        <div className="flex items-center gap-3 shrink-0">
          <LangSwitch lang={c.lang} setLang={c.setLang} className="hidden sm:flex" activeClass={LANG_ACTIVE} idleClass={LANG_IDLE} />
          {nav.account && (
            <a href={nav.account.href} data-testid="nav-client-portal" aria-label={nav.account.label} title={nav.account.label} className="hidden sm:inline-flex items-center justify-center w-10 h-10 rounded-full border opacity-70 hover:opacity-100 transition-opacity" style={{ borderColor: "var(--tpl-primary)" }}>
              <User size={16} />
            </a>
          )}
          {nav.book && (
            <a href={nav.book.href} data-testid="nav-book-cta" className="hidden sm:inline-flex rounded-full px-6 py-2.5 text-sm font-semibold text-white whitespace-nowrap" style={{ background: "var(--tpl-primary)" }}>{nav.book.label}</a>
          )}
          <button
            className="lg:hidden"
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            aria-label={open ? tt("shared.nav.closeMenu") : tt("shared.nav.openMenu")}
          >
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>
      {open && (
        <div className="lg:hidden mx-6 rounded-[2.5rem] rounded-tr-lg p-7 shadow-[0_25px_60px_-35px_rgba(62,42,33,0.6)]" style={{ background: "var(--tpl-secondary)" }} data-testid="mobile-menu">
          <div className="flex flex-col gap-3">
            {links.map((l) => (
              <a key={l.key} href={l.href} onClick={() => setOpen(false)} className="font-semibold opacity-80">{l.label}</a>
            ))}
            {nav.account && (
              <a href={nav.account.href} onClick={() => setOpen(false)} className="inline-flex items-center gap-2 font-semibold opacity-80">
                <User size={16} /> {nav.account.label}
              </a>
            )}
          </div>
          <div className="mt-6 flex items-center justify-between gap-4">
            <LangSwitch lang={c.lang} setLang={c.setLang} activeClass={LANG_ACTIVE} idleClass={LANG_IDLE} />
            {nav.book && (
              <a href={nav.book.href} onClick={() => setOpen(false)} className="rounded-full px-6 py-3 text-sm font-semibold text-white" style={{ background: "var(--tpl-primary)" }}>{nav.book.label}</a>
            )}
          </div>
        </div>
      )}
    </header>
  );
};

/**
 * Was a single "© year brand" line (BUG-026). Now a real sitemap in the theme's idiom: warm
 * secondary ground, one soft-radius card per column, the handwritten Caveat accent.
 */
export const OrganicFooter = ({ toggles = {} }) => {
  const f = useSiteContent(toggles).footerModel;

  return (
    <footer className="px-6 md:px-10 pt-16 pb-10" style={{ background: "var(--tpl-secondary)" }} data-testid="site-footer">
      <div className="max-w-7xl mx-auto grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="p-7 rounded-[2.5rem] rounded-tl-lg bg-[var(--tpl-bg)] shadow-[0_20px_50px_-35px_rgba(62,42,33,0.5)]">
          <div className="tpl-head text-2xl font-semibold flex items-center gap-2">
            <Leaf size={18} style={{ color: "var(--tpl-accent)" }} /> {f.brand}
          </div>
          {/* BUG-210: no dictionary sample any more — an unset tagline is a fact the
              practice hasn't stated, not filler text. */}
          {f.tagline && <p className="mt-3 opacity-70 leading-relaxed text-sm">{f.tagline}</p>}
          {f.socials.length > 0 && (
            <div className="mt-5 flex flex-wrap items-center gap-2.5">
              {f.socials.map((s) => {
                const Icon = SOCIAL_ICONS[s.key];
                return (
                  <a key={s.key} href={s.url} target="_blank" rel="noopener noreferrer" aria-label={s.label} data-testid={`footer-social-${s.key}`} className="w-10 h-10 rounded-full flex items-center justify-center text-white hover:opacity-85 transition-opacity" style={{ background: "var(--tpl-primary)" }}>
                    <Icon size={16} />
                  </a>
                );
              })}
            </div>
          )}
        </div>

        {f.sections.length > 0 && (
          <div className="p-7 rounded-[2.5rem] rounded-br-lg bg-[var(--tpl-bg)] shadow-[0_20px_50px_-35px_rgba(62,42,33,0.5)]">
            <div className="text-xs uppercase tracking-[0.3em] opacity-50">{f.headings.navigate}</div>
            <div className="mt-4 space-y-2">
              {f.sections.map((s) => (
                <a key={s.key} href={s.href} data-testid={`footer-section-${s.key}`} className="block font-semibold opacity-70 hover:opacity-100 transition-opacity">{s.label}</a>
              ))}
            </div>
          </div>
        )}

        {f.pages.length > 0 && (
          <div className="p-7 rounded-[2.5rem] rounded-tr-lg bg-[var(--tpl-bg)] shadow-[0_20px_50px_-35px_rgba(62,42,33,0.5)]">
            <div className="text-xs uppercase tracking-[0.3em] opacity-50">{f.headings.explore}</div>
            <div className="mt-4 space-y-2">
              {f.pages.map((p) => (
                <a key={p.key} href={p.href} data-testid={`footer-page-${p.key}`} className="block font-semibold opacity-70 hover:opacity-100 transition-opacity">{p.label}</a>
              ))}
            </div>
          </div>
        )}

        <div className="p-7 rounded-[2.5rem] rounded-bl-lg bg-[var(--tpl-bg)] shadow-[0_20px_50px_-35px_rgba(62,42,33,0.5)]">
          <div className="text-xs uppercase tracking-[0.3em] opacity-50">{f.headings.contact}</div>
          <div className="mt-4 space-y-2">
            <a href={`mailto:${f.contact.email}`} className="block break-all font-semibold opacity-70 hover:opacity-100 transition-opacity">{f.contact.email}</a>
            <div className="opacity-70">{f.contact.place}</div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto mt-10 flex items-center justify-between flex-wrap gap-3 text-sm opacity-60" data-testid="footer-copyright">
        <span>© {f.year} {f.brand} — {f.rights}</span>
        <span style={SCRIPT} className="text-2xl">{f.built}</span>
      </div>
    </footer>
  );
};

/** The chrome contract templates/registry.js registers for t3_warm_organic. */
export const ORGANIC_CHROME = {
  Header: OrganicHeader,
  Footer: OrganicFooter,
  mainClass: ORGANIC_MAIN_CLASS,
};

export default ORGANIC_CHROME;
